import React, { useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { Spinner } from '@/components/ui/spinner';
import { ListGroup } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { toISODateOnly } from '../../../../../utils/dateUtils';
import { OptionBase } from '../../../../../components/Select/types';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import {
  RenderOptionParams,
  SelectSingleBase,
} from '../../../components/Selects/SelectSingleBase';
import {
  useCurrentTrackingProjectsQuery,
  useUpdateMyTimeEntryTrackingProjectMutation,
} from '../../../__generated__/graphql';
import { registerTrackingProjectUpdate } from '../../../hooks/timeTracking/trackingProjectUpdates';
import { readMyTimeEntryTimeTrackingFromCache } from '../../../utils/apolloCacheHelpers';

const i18Path = 'timeTracking.current.projectSelect';

type Props = {
  timeEntryId: string;
  selectedProjectId: string | null;
  // Session start; used only to filter the dropdown by the project's active
  // window. The authoritative booking day is computed server-side.
  startDatetime: string;
};

type ProjectOption = {
  Id: string;
  flair__Name__c: string;
  flair__Start_Date__c?: string | null;
  flair__End_Date__c?: string | null;
};

const ProjectSelectStep: React.FC<React.PropsWithChildren<Props>> = ({
  timeEntryId,
  selectedProjectId,
  startDatetime,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const errorHandler = useMutationErrorHandler();
  const client = useApolloClient();
  const { data } = useCurrentTrackingProjectsQuery();
  const [updateTrackingProject, { loading: updating }] =
    useUpdateMyTimeEntryTrackingProjectMutation();

  const options: OptionBase[] = useMemo(() => {
    const trackedDate = toISODateOnly(new Date(startDatetime));
    const today = toISODateOnly(new Date());
    return (data?.projects ?? [])
      .filter(
        (project) =>
          project.Id === selectedProjectId ||
          isProjectActive(project, trackedDate) ||
          isProjectActive(project, today),
      )
      .map((project) => ({
        value: project.Id,
        label: project.flair__Name__c,
      }));
  }, [data?.projects, selectedProjectId, startDatetime]);

  const cacheId = client.cache.identify({
    __typename: 'MyTimeEntry',
    Id: timeEntryId,
  });

  const readProjectFromCache = useCallback(
    (): string | null =>
      readMyTimeEntryTimeTrackingFromCache(client.cache, timeEntryId)
        ?.flair__Tracking_Project__c ?? null,
    [client, timeEntryId],
  );

  // Reflect the pick in the cache immediately so the value shows without
  // waiting on the round-trip and the clock-out guard reads a consistent state.
  const writeProjectToCache = useCallback(
    (projectId: string | null) => {
      client.cache.modify({
        id: cacheId,
        fields: { flair__Tracking_Project__c: () => projectId },
      });
    },
    [client, cacheId],
  );

  const handleChange = useCallback(
    (projectId: string | null) => {
      const previous = readProjectFromCache();
      writeProjectToCache(projectId);
      const promise = updateTrackingProject({
        variables: { input: { timeEntryId, projectId } },
      }).catch((error) => {
        // Only roll back if a newer pick hasn't already replaced this value.
        if (readProjectFromCache() === projectId) {
          writeProjectToCache(previous);
        }
        errorHandler(error);
      });
      registerTrackingProjectUpdate(timeEntryId, promise);
    },
    [
      updateTrackingProject,
      timeEntryId,
      readProjectFromCache,
      writeProjectToCache,
      errorHandler,
    ],
  );

  const renderOption = useCallback(
    ({ option }: RenderOptionParams<OptionBase>) => <span>{option.label}</span>,
    [],
  );

  return (
    <ListGroup.Item>
      <div className="d-flex align-items-center mb-2">
        <h5 className="mb-0">{t('label')}</h5>
        {updating && (
          <Spinner
            animation="border"
            size="sm"
            className="ms-2 text-muted"
            role="status"
          />
        )}
      </div>
      <SelectSingleBase
        value={selectedProjectId}
        onChange={handleChange}
        options={options}
        renderOption={renderOption}
        placeholder={t('placeholder')}
        heightMode="large"
        isDisabled={updating}
      />
    </ListGroup.Item>
  );
};

function isProjectActive(project: ProjectOption, day: string): boolean {
  const start = project.flair__Start_Date__c?.slice(0, 10);
  const end = project.flair__End_Date__c?.slice(0, 10);
  if (start && day < start) {
    return false;
  }
  if (end && day > end) {
    return false;
  }
  return true;
}

export default ProjectSelectStep;
